<script lang="ts" setup>
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import MediaList from '@/components/media/MediaList.vue';
import { Category, titleRules } from '@/models/category.model';
import { MediaPurpose } from '@/models/media.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { CategoriesActions, CategoriesState } from '@/stores/categories.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';

const store = useStore<ShiftCMSState & { cs: CategoriesState }>();
const events = useEvents();

const props = defineProps({
  data: {
    type: Object as PropType<Category>,
  },
});

const valid = ref<boolean>(false);

const category = ref<Category>({
  id: NEW_ID,
  index: 0,
  title: '',
  tileTitle: '',
  color: '#979797',
  emoji: '🌱',
  media: [],
  isContent: true,
  parentCategory: null,
});

const action = computed<string>(() =>
  category.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveCategory = async () => {
  let success: boolean = false;
  if (valid.value) {
    if (category.value.id === NEW_ID) {
      success = await store.dispatch(
        CategoriesActions.AddCategory,
        category.value,
      );
    } else {
      success = await store.dispatch(
        CategoriesActions.UpdateCategory,
        category.value,
      );
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved category',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(CategoriesActions.GetCategories);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Failed to save category',
      type: 'error',
    });
  }
};

const updateLanguage = async () => {
  if (category.value.id === NEW_ID) {
    Object.assign(category, { title: '' });
  } else {
    const cat = await store.dispatch(
      CategoriesActions.GetCategory,
      category.value.id,
    );

    if (cat) {
      Object.assign(category.value, { ...cat });
    }
  }
};

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);
  if (props.data) {
    Object.assign(category.value, props.data);
  }
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

const removeCategory = async () => {
  if (
    category.value &&
    category.value.id !== NEW_ID &&
    confirm('Are you absolutely sure you want to delete this "Category"?')
  ) {
    store.dispatch(CategoriesActions.DeleteCategory, category.value.id);

    store.commit(Mutations.HideDrawer);
  }
};
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveCategory">
    <v-container>
      <h2>{{ $t(action) }} a category <CurrentLanguage /></h2>
      <v-row>
        <v-col class="mx-auto">
          <v-text-field
            v-model="category.title"
            :rules="titleRules"
            label="Title"
            required
          ></v-text-field>
        </v-col>
        <v-col class="mx-auto" width="5%" cols="2">
          <v-text-field
            v-model="category.emoji"
            label="Emoji"
            required
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="mx-auto">
          <v-text-field
            v-model="category.tileTitle"
            :rules="titleRules"
            label="Tile title"
            hint="Used on the home screen, when choosing the topic to focus on"
            required
          ></v-text-field>
        </v-col>
        <v-col cols="2" style="position: relative">
          <v-dialog width="auto">
            <template v-slot:activator="{ props: activatorProps }">
              <label
                style="
                  position: absolute;
                  top: 0;
                  left: 0;
                  color: #767676;
                  font-size: 13px;
                "
                >Color</label
              >
              <div
                v-bind="activatorProps"
                :style="`margin-top: 4px; height: 60%; width: 100%; background-color: ${category.color}`"
              ></div>
            </template>
            <template v-slot:default="{}">
              <v-color-picker v-model="category.color"></v-color-picker>
            </template>
          </v-dialog>
        </v-col>
      </v-row>

      <v-row>
        <v-col align="right">
          <v-checkbox label="Content" v-model="category.isContent" />
        </v-col>
        <v-col v-if="category.isContent"
          >A <i>content category</i> is a category that can be interacted with
          by the user. It is an option when choosing interests, for example.
        </v-col>
        <v-col v-if="!category.isContent"
          >A <strong>non-</strong><i>content category</i> is a category that
          cannot be interacted with by the user. It is used to show certain co2
          values that the user cannot change.
        </v-col>
      </v-row>
      <MediaList
        v-model="category"
        :required="[MediaPurpose.BackgroundImage]"
      />
      <v-row>
        <v-col col="2"></v-col>
        <v-col>
          <v-btn
            class="mt-2"
            type="button"
            color="warning"
            @click="removeCategory"
            block
            >Delete</v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block>{{
            action
          }}</v-btn>
        </v-col>
        <v-col col="2"></v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
